import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex, Skeleton } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { setFiltersAction, setTeamAction } from 'actions/globalFilters';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import { last } from 'lodash';
import TeamModel from 'models/team';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { getDefaultLocalStorageStatsFilters, useTeamPageState } from 'screens/Team';
import buildTeamPageFilters from 'screens/Team/TeamPageFilters/buildTeamPageFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { okrsPageSelector } from 'selectors/okrsPage';
import { StyledTooltip } from 'styles/common';

import { CollapseAll, ExpandAll, Refresh2 } from 'assets/assetsV1/iconComponents';

import { OKR_TYPES, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { sendCreatedObjectiveEvent, sendCreatedKREvent } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';
// import { sidebarNavCohortSelector } from 'selectors/sidebarNav';
import { Paper } from 'uiToolkitV1/Paper';
import { Tooltip } from 'uiToolkitV1/Tooltip';

import { AddNewGoalObjectiveButton } from '../../AddNewGoalObjectiveButton';
import { GoalsTreeViewTable } from '../GoalsTreeViewTable';
import {
  goalsCascadedViewReactQueryKey,
  useDepartmentChildFunctionsIds,
  useFetchDepartmentHierarchy,
} from '../hooks/dataHooks';
import { DepartmentFunctionHierarchySelect } from './DepartmentFunctionHierarchySelect';

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const generateAttributeFilter = (activeFilters = []) => {
  const data = activeFilters.map(({ value }) => value);
  return {
    type: 'STATUS',
    operatorType: 'IN',
    data,
  };
};

const OKRSidePanels = ({
  isOpenCreateGoalPanel,
  setIsOpenCreateGoalPanel,
  isOpenCreateObjectivePanel,
  setIsOpenCreateObjectivePanel,
  selectedTimelines,
  afterGoalCreation,
}) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const [isGoalPanelOfCreatedObjective, setIsGoalPanelOfCreatedObjective] = useState(false);
  // timebound details of newly created objective
  const [timeboundDetailsOfCreatedObjective, setTimeboundDetailsOfCreatedObjective] = useState(null);

  return (
    <>
      <CreateGoalPanel
        open={isOpenCreateGoalPanel}
        onCancel={() => {
          setIsOpenCreateGoalPanel(false);
        }}
        employee={loggedInEmployee}
        // defaultTask={defaultCreateTask}
        subObjective={isGoalPanelOfCreatedObjective ? { ...timeboundDetailsOfCreatedObjective } : null}
        source="teampage"
        eventSource="Team Page"
        selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
        afterGoalCreation={afterGoalCreation}
      />

      <ObjectiveSidePanel
        open={isOpenCreateObjectivePanel}
        onCancel={() => {
          setTimeboundDetailsOfCreatedObjective(null);
          setIsGoalPanelOfCreatedObjective(false);
          setIsOpenCreateObjectivePanel(false);
        }}
        selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
        isAlignmentAtParentLevel
        setIsOpenCreateGoalPanel={(value) => {
          setIsOpenCreateGoalPanel(value);
          setIsGoalPanelOfCreatedObjective(value);
        }}
        setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
        source="Team Page"
      />
    </>
  );
};

const DepartmentViewContainerWithoutDepartmentHierarchy = ({ isHierarchyView = false, departmentHierarchy }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const page = useViewPageName();
  const gridRef = useRef();

  const sideNavSelectedCohort = useSelectedView();
  const { selectedTimelines } = useSelector(okrsPageSelector);

  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);

  const { id: paramsTeamId, name: teamName } = sideNavSelectedCohort ?? {};

  const [selectedDepartment, setSelectedDepartment] = useState(last(departmentHierarchy) ?? {});

  const { data: departmentChildFunctionsIds } = useDepartmentChildFunctionsIds({
    functionId: selectedDepartment?.id,
  });

  const departmentIdsToFilter = useMemo(
    () => (selectedDepartment?.id ? [selectedDepartment.id, ...(departmentChildFunctionsIds ?? [])] : []),
    [departmentChildFunctionsIds, selectedDepartment]
  );

  const handleDepartmentChange = (department) => {
    setSelectedDepartment(department);
  };

  const { team, setTeam, activeFilters, selectedTaskType, activeFiltersDispatch, newFiltersState } = useTeamPageState({
    sideNavSelectedCohort,
  });

  const attributeFilter = generateAttributeFilter(activeFilters);

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: GROUP_BY,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [selectedTimelines?.[0]],
        attributeFilter,
        isHierarchyView,
        pageType: 'TEAM_HUB',
      })
    );
  };

  const isDepartmentFilterPresent = departmentIdsToFilter.length > 0;

  const applyDepartmentIdFilter = useCallback(() => {
    const isDepartmentFilterPresent = departmentIdsToFilter.length > 0;

    if (!isDepartmentFilterPresent || !gridRef.current?.api) return;
    const currentFilter = gridRef.current.api.getFilterModel();
    const newFilterObj = {
      ...currentFilter,
      'ag-Grid-AutoColumn': {
        filterType: 'multi',
        filterModels: [
          currentFilter?.['ag-Grid-AutoColumn']?.[0] ?? null,
          {
            values: departmentIdsToFilter.map((id) => id.toString()),
            filterType: 'set',
          },
          currentFilter?.['ag-Grid-AutoColumn']?.[2] ?? null,
          currentFilter?.['ag-Grid-AutoColumn']?.[3] ?? null,
        ],
      },
    };

    gridRef.current.api.setFilterModel(newFilterObj);
    gridRef.current.api.onFilterChanged();
  }, [gridRef, departmentIdsToFilter]);

  const onFirstDataRendered = useCallback(() => {
    applyDepartmentIdFilter();
  }, [applyDepartmentIdFilter]);

  useEffect(() => {
    applyDepartmentIdFilter();
  }, [applyDepartmentIdFilter]);

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [dispatch, setTeam, paramsTeamId, teamName]);

  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, true);
    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(TASK_TYPES_2.goal) || [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
  }, [JSON.stringify(newFiltersState)]);

  const RefreshIconComponent = () => <Refresh2 boxSize={8} />;

  if (!sideNavSelectedCohort || !team || !team?.id) {
    return <Skeleton height="30vh" />;
  }
  return (
    <>
      <Box width="100%" p="16px">
        <Paper p="24px">
          <Flex ml="auto" alignItems="center" mb="22px">
            <DepartmentFunctionHierarchySelect
              options={departmentHierarchy}
              selectedOption={selectedDepartment}
              onChange={handleDepartmentChange}
            />
            <Flex
              sx={{
                span: {
                  marginLeft: 'auto',
                },
              }}
              w="100%"
              alignItems="center"
              mr="16px"
            >
              <AddNewGoalObjectiveButton
                onAddKRClick={() => {
                  dispatch(
                    openCreateOKRPanel({
                      selectedObjective: null,
                      selectedTimeBoundPeriod: selectedTimelines?.[0],
                      okrPanelType: OKR_TYPES.KEY_RESULT,
                      onKeyResultCreation(okrDetails) {
                        sendCreatedKREvent({
                          eventProperties: {
                            source: 'Add New Button',
                            page,
                            goalId: okrDetails?.uuid,
                          },
                        });
                        refetchCascadedViewData();
                      },
                      onObjectiveCreation(okrDetails) {
                        sendCreatedObjectiveEvent({
                          eventProperties: {
                            source: 'Add New Button',
                            page,
                            objectiveId: okrDetails?.uuid,
                            description: okrDetails?.description,
                            level: okrDetails?.objectiveLevel,
                            type: okrDetails?.type,
                            timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                          },
                        });
                        refetchCascadedViewData();
                      },
                      history,
                      location,
                    })
                  );
                }}
                onAddObjectiveClick={() => {
                  dispatch(
                    openCreateOKRPanel({
                      selectedObjective: null,
                      selectedTimeBoundPeriod: selectedTimelines?.[0],
                      okrPanelType: OKR_TYPES.OBJECTIVE,
                      onObjectiveCreation(okrDetails) {
                        sendCreatedObjectiveEvent({
                          eventProperties: {
                            source: 'Add New Button',
                            page,
                            objectiveId: okrDetails?.uuid,
                            description: okrDetails?.description,
                            level: okrDetails?.objectiveLevel,
                            type: okrDetails?.type,
                            timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                          },
                        });
                        refetchCascadedViewData();
                      },
                      onKeyResultCreation(okrDetails) {
                        sendCreatedKREvent({
                          eventProperties: {
                            source: 'Add New Button',
                            page,
                            goalId: okrDetails?.uuid,
                          },
                        });
                        refetchCascadedViewData();
                      },
                      history,
                      location,
                    })
                  );
                }}
              />
            </Flex>
            {/*  */}
            {/* <Box mr={1}>
              <Button
                StartIcon={RefreshIconComponent}
                onClick={() => queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData])}
                variant="tertiary"
              />
            </Box> */}
            <Tooltip label="Collapse all">
              <Button
                StartIcon={CollapseAll}
                onClick={() => {
                  gridRef?.current?.api.forEachNode((node) => {
                    gridRef?.current?.api.setRowNodeExpanded(node, false);
                  });
                }}
                variant="tertiary"
              />
            </Tooltip>
            <StyledTooltip title="Expand all">
              <Button
                StartIcon={ExpandAll}
                onClick={() => {
                  gridRef?.current?.api.forEachNode((node) => {
                    gridRef?.current?.api.setRowNodeExpanded(node, true);
                  });
                }}
                variant="tertiary"
                mr="16px"
              />
            </StyledTooltip>
          </Flex>

          <GoalsTreeViewTable
            attributeFilter={attributeFilter}
            timeBoundPeriod={selectedTimelines?.[0] ?? null}
            isHierarchyView
            hasImplicitDepartmentFilter={isDepartmentFilterPresent}
            departmentIdsToFilter={departmentIdsToFilter ?? []}
            onFirstDataRendered={onFirstDataRendered}
            gridRef={gridRef}
          />
        </Paper>
      </Box>
      <OKRSidePanels
        isOpenCreateGoalPanel={isOpenCreateGoalPanel}
        setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
        isOpenCreateObjectivePanel={isOpenCreateObjectivePanel}
        setIsOpenCreateObjectivePanel={setIsOpenCreateObjectivePanel}
        selectedTimelines={selectedTimelines}
        afterGoalCreation={refetchCascadedViewData}
      />
    </>
  );
};

const DepartmentViewContainer = ({ isHierarchyView = false }) => {
  const { data: departmentHierarchy, isLoading, isSuccess } = useFetchDepartmentHierarchy();

  if (!isSuccess) return null;

  return (
    <DepartmentViewContainerWithoutDepartmentHierarchy
      isHierarchyView={isHierarchyView}
      departmentHierarchy={departmentHierarchy}
    />
  );
};

export { DepartmentViewContainer };
